import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Target, CheckCircle2, Trophy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useEffect, useState, useRef } from "react";

interface Quest {
  id: string;
  title: string;
  slug: string;
  xp: number;
  min_level: number;
}

interface UserQuest {
  quest_id: string;
  completed_at: string | null;
}

interface QuestPanelProps {
  quests: Quest[];
  userQuests: UserQuest[];
  userId: string;
  onQuestUpdate: () => void;
}

export const QuestPanel = ({ quests, userQuests, userId, onQuestUpdate }: QuestPanelProps) => {
  const [userStats, setUserStats] = useState<any>(null);
  const previousProgressRef = useRef<{[questId: string]: number}>({});

  useEffect(() => {
    const fetchUserStats = async () => {
      if (!userId) return;

      try {
        // Fetch comprehensive user stats for progress calculations
        const [
          { data: xpData },
          { data: streakData },
          { data: messagesData },
          { data: servicesData },
          { data: reviewsData },
          { data: chatRoomsData },
          { data: completedServicesData },
          { data: profileData },
          { data: portfolioData }
        ] = await Promise.all([
          supabase.from('user_xp').select('xp, level').eq('user_id', userId).maybeSingle(),
          supabase.from('user_streaks').select('current_streak').eq('user_id', userId).maybeSingle(),
          supabase.from('messages').select('id', { count: 'exact' }).eq('sender_id', userId),
          supabase.from('services').select('id', { count: 'exact' }).eq('user_id', userId),
          supabase.from('reviews').select('id', { count: 'exact' }).eq('provider_id', userId),
          supabase.from('chat_room_members').select('id', { count: 'exact' }).eq('user_id', userId),
          supabase.from('conversations').select('id', { count: 'exact' }).or(`participant1_id.eq.${userId},participant2_id.eq.${userId}`).eq('service_completed', true),
          supabase.from('profiles').select('first_name, last_name, bio, avatar_url').eq('id', userId).maybeSingle(),
          supabase.from('portfolio_items').select('id', { count: 'exact' }).eq('user_id', userId)
        ]);

        setUserStats({
          xp: xpData?.xp || 0,
          level: xpData?.level || 0,
          streak: streakData?.current_streak || 0,
          messages: messagesData?.length || 0,
          services: servicesData?.length || 0,
          reviews: reviewsData?.length || 0,
          chatRooms: chatRoomsData?.length || 0,
          completedServices: completedServicesData?.length || 0,
          profile: profileData,
          portfolioItems: portfolioData?.length || 0
        });
      } catch (error) {
        console.error('Error fetching user stats:', error);
      }
    };

    fetchUserStats();
  }, [userId]);

  // Check for quest completion and automatically award XP
  useEffect(() => {
    if (!userStats || !userId) return;

    const checkForCompletedQuests = async () => {
      for (const quest of quests) {
        const dbCompleted = userQuests.find(uq => uq.quest_id === quest.id)?.completed_at != null;
        
        if (!dbCompleted) {
          const progress = getQuestProgress(quest.slug);
          const previousProgress = previousProgressRef.current[quest.id] || 0;
          
          // If quest just reached 100% (wasn't 100% before)
          if (progress.percentage >= 100 && previousProgress < 100) {
            try {
              // Award XP for this specific quest
              await supabase.rpc('complete_quest_if_not_exists', {
                target_user_id: userId,
                target_quest_id: quest.id,
                quest_xp: quest.xp
              });

              toast.success(`Quest "${quest.title}" completed! +${quest.xp} XP`, {
                duration: 4000,
              });

              // Update quest completion status
              onQuestUpdate();
            } catch (error) {
              console.error('Error completing quest:', error);
            }
          }
          
          // Update previous progress
          previousProgressRef.current[quest.id] = progress.percentage;
        }
      }
    };

    checkForCompletedQuests();
  }, [userStats, quests, userQuests, userId, onQuestUpdate]);

  const isQuestCompleted = (questId: string) => {
    const userQuest = userQuests.find(uq => uq.quest_id === questId);
    return userQuest?.completed_at != null;
  };

  const getQuestDescription = (slug: string): string => {
    const descriptions: Record<string, string> = {
      'welcome': 'Complete your onboarding to AnySkill',
      'complete_profile': 'Fill out your name, bio, and other profile details',
      'first_service': 'Create and publish your first service offering',
      'first_service_image': 'Add an image to make your service more appealing',
      'first_review': 'Receive your first customer review',
      'first_chat': 'Start a conversation with another user',
      'social_butterfly': 'Actively engage in community conversations',
      'message_master': 'Become a communication expert',
      'join_chat_rooms': 'Join community discussions in various rooms',
      'service_provider': 'Build a diverse portfolio of services',
      'complete_service': 'Successfully complete a service transaction',
      'service_expert': 'Demonstrate expertise through multiple completions',
      'community_helper': 'Help many different community members',
      'xp_collector': 'Accumulate experience through various activities',
      'level_up_expert': 'Reach higher experience levels',
      'streak_master': 'Maintain consistent daily activity',
      'first_avatar': 'Upload a profile picture to personalize your account',
      'write_bio': 'Tell others about yourself in your bio section',
      'make_friend': 'Connect with another community member',
      'explore_categories': 'Browse different service categories available',
      'host_chat_room': 'Create your own community chat room',
      'first_like': 'Receive positive feedback on your content',
      'share_portfolio': 'Showcase your work through portfolio items',
      'helpful_review': 'Leave constructive feedback for others',
      'master_category': 'Become an expert in a specific service area',
      'power_user': 'Demonstrate advanced platform engagement',
      'build_reputation': 'Establish yourself as a trusted community member',
      'teach_others': 'Share knowledge and help others learn',
      'community_leader': 'Take on leadership roles in the community',
      'top_leaderboard': 'Reach the top rankings in community activity',
      'perfect_services': 'Deliver exceptional service quality consistently',
      'xp_master_5k': 'Achieve mastery through extensive experience',
      'month_active': 'Maintain engagement for a full month',
      'help_25_people': 'Make a significant impact by helping many users',
      'signature_service': 'Develop a service that becomes your specialty',
      'mentor_figure': 'Guide and support other community members'
    };
    return descriptions[slug] || 'Complete this quest to earn XP and advance your progress';
  };

  const getQuestProgress = (slug: string): { current: number; target: number; percentage: number } => {
    if (!userStats) return { current: 0, target: 1, percentage: 0 };

    const progressData: Record<string, { current: number; target: number }> = {
      'complete_profile': {
        current: (userStats.profile?.first_name ? 1 : 0) + 
                 (userStats.profile?.last_name ? 1 : 0) + 
                 (userStats.profile?.bio && userStats.profile.bio.length > 10 ? 1 : 0) + 
                 (userStats.profile?.avatar_url ? 1 : 0),
        target: 4
      },
      'first_service': { current: Math.min(userStats.services, 1), target: 1 },
      'first_chat': { current: Math.min(userStats.messages, 1), target: 1 },
      'social_butterfly': { current: Math.min(userStats.messages, 10), target: 10 },
      'message_master': { current: Math.min(userStats.messages, 50), target: 50 },
      'join_chat_rooms': { current: Math.min(userStats.chatRooms, 3), target: 3 },
      'service_provider': { current: Math.min(userStats.services, 5), target: 5 },
      'complete_service': { current: Math.min(userStats.completedServices, 1), target: 1 },
      'service_expert': { current: Math.min(userStats.completedServices, 5), target: 5 },
      'xp_collector': { current: Math.min(userStats.xp, 1000), target: 1000 },
      'level_up_expert': { current: Math.min(userStats.level, 5), target: 5 },
      'streak_master': { current: Math.min(userStats.streak, 14), target: 14 },
      'first_avatar': { current: userStats.profile?.avatar_url ? 1 : 0, target: 1 },
      'write_bio': { current: userStats.profile?.bio && userStats.profile.bio.length > 10 ? 1 : 0, target: 1 },
      'share_portfolio': { current: Math.min(userStats.portfolioItems, 3), target: 3 },
      'first_review': { current: Math.min(userStats.reviews, 1), target: 1 },
      'xp_master_5k': { current: Math.min(userStats.xp, 5000), target: 5000 }
    };

    const data = progressData[slug] || { current: 0, target: 1 };
    const percentage = Math.min((data.current / data.target) * 100, 100);
    
    return { ...data, percentage };
  };

  const completeQuest = async (quest: Quest) => {
    // Run the auto-complete function for this user to refresh quest status and badges
    try {
      await supabase.rpc('auto_complete_quests_for_user', {
        target_user_id: userId
      });

      // Also check for new badges
      await supabase.rpc('auto_check_and_award_badges', {
        target_user_id: userId
      });

      toast.success("Quest progress updated!", {
        duration: 3000,
      });
      onQuestUpdate();
    } catch (error) {
      console.error('Error updating quest progress:', error);
      toast.error("Failed to update quest progress.");
    }
  };

  const completedCount = userQuests.filter(uq => uq.completed_at != null).length + 
    quests.filter(quest => {
      const dbCompleted = userQuests.find(uq => uq.quest_id === quest.id)?.completed_at != null;
      const progress = getQuestProgress(quest.slug);
      return !dbCompleted && progress.percentage >= 100;
    }).length;
  const totalQuests = quests.length;

  return (
    <Card className="bg-black/20 backdrop-blur-sm border-blue-500/50 shadow-2xl">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Target className="w-5 h-5" />
          Quests
          <Badge className="ml-auto bg-blue-500 text-white">
            {completedCount}/{totalQuests} completed
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {quests.length === 0 ? (
          <div className="text-center py-8">
            <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400">No quests available yet. Check back later!</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {quests.map((quest) => {
              const completed = isQuestCompleted(quest.id);
              const progress = getQuestProgress(quest.slug);
              // Consider quest completed if it's marked as completed OR progress is 100%
              const isActuallyCompleted = completed || progress.percentage >= 100;
              
              return (
                <div
                  key={quest.id}
                  className={`p-4 rounded-lg border transition-all duration-300 ${
                    isActuallyCompleted
                      ? 'bg-green-900/30 border-green-500/50'
                      : 'bg-gray-900/30 border-gray-500/50'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {isActuallyCompleted && <CheckCircle2 className="w-4 h-4 text-green-400" />}
                        <h3 className="text-white font-bold">{quest.title}</h3>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-blue-200 border-blue-400">
                          +{quest.xp} XP
                        </Badge>
                        {quest.min_level > 0 && (
                          <Badge variant="outline" className="text-yellow-200 border-yellow-400">
                            Level {quest.min_level}+ Required
                          </Badge>
                        )}
                      </div>
                      <p className="text-gray-300 text-sm">
                        {getQuestDescription(quest.slug)}
                      </p>
                    </div>
                    <div className="ml-4 flex flex-col items-end">
                      {isActuallyCompleted ? (
                        <Badge className="bg-green-500 text-white mb-2">
                          ✓ Completed
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-400 border-gray-500 mb-2">
                          In Progress
                        </Badge>
                      )}
                      {!isActuallyCompleted && (
                        <div className="w-32">
                          {(() => {
                            const progress = getQuestProgress(quest.slug);
                            return (
                              <div className="space-y-1">
                                <div className="flex justify-between text-xs text-gray-400">
                                  <span>{progress.current}</span>
                                  <span>{progress.target}</span>
                                </div>
                                <Progress 
                                  value={progress.percentage} 
                                  className="h-2 bg-gray-700/50"
                                />
                                <div className="text-xs text-gray-500 text-center">
                                  {Math.round(progress.percentage)}%
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};